/**
 * Adapter - IdentityDocumentGateway
 *
 * Appelle le backend CardLife pour vérifier ou sauvegarder un document d'identité.
 *
 * Endpoint : POST /api/v1/documents-identites/check-or-save
 *
 * Utilisé lors de la vérification d'identité pour :
 * - Vérifier si le document existe déjà
 * - Créer un nouveau document si non existant
 * - Mettre à jour un document existant
 */

const ENV = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env ?? {};
const CARDLIFE_BASE_URL = (ENV.VITE_CARDLIFE_BASE_URL ?? '').replace(/\/+$/g, '');
const IDENTITY_DOCUMENT_ENDPOINT = '/api/v1/document-identites/check-or-save';

// DTO pour l'envoi du document d'identité
export interface IdentityDocumentDTO {
  id?: number;
  typeDocument: string;         // CNI, PASSPORT, DRIVER_LICENSE, CONSULAR_CARD
  numeroDocument: string;
  dateExpiration: string;        // ISO string (YYYY-MM-DD)
  dateDelivrance: string;        // ISO string (YYYY-MM-DD)
  adresse?: string;
  numeroCompte?: string;         // Numéro de compte du client
  createdAt?: {
    year?: number;
    month?: number;
    day?: number;
  };
  updatedAt?: {
    year?: number;
    month?: number;
    day?: number;
  };
  createdBy?: string;
  updatedBy?: string;
  carteId?: number;
  /** Carte authentifiée par empreinte : la pièce doit appartenir à l'un de ses comptes */
  numeroCarte: string;
  /** Code du caissier (requis par le CBS pour lister les comptes de la carte) */
  codeUser: string;
}

// Response de l'API
export interface IdentityDocumentResponse {
  status?: string;           // "EXISTING" | "CREATED" | "UPDATED" | "A_RATTACHER"
  message?: string;          // "Pièce conforme à la base de donnée" etc.
  id: number;
  typeDocument: string;
  numeroDocument: string;
  dateExpiration: string;
  dateDelivrance: string;
  adresse?: string;
  numeroCompte?: string;     // Numéro de compte du client
  carteId: number;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
}

export class IdentityDocumentGateway {
  /**
   * Vérifie ou sauvegarde un document d'identité
   *
   * @param document - Données du document d'identité
   * @returns Document sauvegardé avec son ID
   */
  async checkOrSaveDocument(
    document: IdentityDocumentDTO
  ): Promise<IdentityDocumentResponse> {
    // Ne pas envoyer de token JWT car l'endpoint est en .permitAll()
    // const authHeader = buildBearerAuthHeader();
    const url = `${CARDLIFE_BASE_URL}${IDENTITY_DOCUMENT_ENDPOINT}`;

    // Envoyer uniquement les champs métier — le backend gère createdAt/updatedAt/createdBy
    const payload: Record<string, any> = {
      // Le backend attend son propre enum TypeDocument (PASSEPORT, PERMIS…) : envoyer PASSPORT
      // faisait échouer la lecture de la requête (400 « Failed to read request »).
      typeDocument: IdentityDocumentGateway.mapDocumentType(document.typeDocument),
      numeroDocument: document.numeroDocument.trim(),
      dateExpiration: document.dateExpiration,
      dateDelivrance: document.dateDelivrance,
      adresse: document.adresse ?? '',
      numeroCarte: document.numeroCarte,
      codeUser: document.codeUser,
    };

    // Ajouter le numéro de compte si disponible
    if (document.numeroCompte) {
      payload.numeroCompte = document.numeroCompte;
    }

    // N'envoyer carteId que s'il est valide (> 0)
    if (document.carteId && document.carteId > 0) {
      payload.carteId = document.carteId;
    }

    try {
      console.log('[IdentityDocument] Envoi document:', {
        type: payload.typeDocument,
        numero: payload.numeroDocument,
        carteId: payload.carteId,
      });

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          // Pas d'Authorization header car endpoint en .permitAll()
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const text = await response.text();
        console.error('[IdentityDocument] Erreur HTTP:', response.status, text);

        // Gestion spécifique des erreurs JWT/Auth
        if (response.status === 401 || response.status === 403) {
          throw new Error('Erreur d\'authentification. Veuillez vous reconnecter.');
        }

        throw new Error(messageErreurDocument(response.status, text));
      }

      const json = await response.json();
      // Le backend retourne { status: "EXISTING"|"CREATED", message: "...", data: {...} }
      const docData = json.data ?? json;
      console.log('[IdentityDocument] Résultat:', json.status, '- ID:', docData.id);

      return { ...docData, status: json.status, message: json.message } as IdentityDocumentResponse;
    } catch (error) {
      console.error('[IdentityDocument] Erreur lors de l\'appel:', error);
      throw error;
    }
  }

  /**
   * Mapper le type de document depuis le format frontend vers le format backend
   */
  static mapDocumentType(frontendType: string): string {
    // Valeurs de l'enum backend ci.unacoopec.cardlife.domain.enumeration.TypeDocument
    const mapping: Record<string, string> = {
      CNI: 'CNI',
      PASSPORT: 'PASSEPORT',
      PASSEPORT: 'PASSEPORT',
      DRIVER_LICENSE: 'PERMIS',
      PERMIS: 'PERMIS',
      CONSULAR_CARD: 'CARTE_CONSULAIRE',
      CARTE_CONSULAIRE: 'CARTE_CONSULAIRE',
    };
    return mapping[frontendType] ?? frontendType;
  }

  /**
   * Formater une date en string ISO (YYYY-MM-DD)
   */
  static formatDateISO(date: Date): string {
    return date.toISOString().split('T')[0];
  }
}

/** Message lisible pour le caissier à partir de la réponse d'erreur du backend. */
function messageErreurDocument(status: number, corps: string): string {
  let detail = '';
  try {
    const json = JSON.parse(corps);
    detail = json.userMessage || '';
  } catch {
    // corps non JSON
  }
  if (detail) return detail;
  if (status === 400) return "La pièce d'identité n'a pas pu être enregistrée : vérifiez le type, le numéro et les dates saisis.";
  if (status >= 500) return "Le serveur n'a pas pu enregistrer la pièce d'identité. Réessayez dans quelques instants.";
  return `Enregistrement de la pièce impossible (erreur ${status}).`;
}
